import { writable, type Writable } from 'svelte/store';
import { noSpotify } from '../spotify/flags';

/** The TV window is a 16:9 pop-up the host casts with Chrome's "Cast tab". */
export const TV_WIDTH = 1920;
export const TV_HEIGHT = 1080;
const TV_KEY = 'banger.tv';
const FAST_KEY = 'banger.fast';
const WINDOW_NAME = 'banger-tv';

export interface TvFlags { nospotify?: boolean; fast?: boolean }

/** `/?as=host&tv=1` on this origin; keeps the dev flags (`nospotify`, `fast`) that are on. */
export function buildTvUrl(origin: string, search: string, stored: TvFlags = {}): string {
  const q = new URLSearchParams(search);
  const out = new URLSearchParams({ as: 'host', tv: '1' });
  if (stored.nospotify || q.get('nospotify') === '1') out.set('nospotify', '1');
  if (stored.fast || q.get('fast') === '1') out.set('fast', '1');
  return `${origin}/?${out}`;
}

export interface TvMeasure {
  innerW: number; innerH: number; outerW: number; outerH: number;
  availW: number; availH: number;
}
export interface TvTarget { viewportW: number; viewportH: number; outerW: number; outerH: number }

/**
 * Outer window size that gives the largest 16:9 viewport up to 1920x1080.
 * Exactly 1920x1080 when the screen can hold it; otherwise the largest exact-16:9 fit.
 */
export function computeTvTarget(m: TvMeasure): TvTarget {
  const dw = Math.max(0, m.outerW - m.innerW);
  const dh = Math.max(0, m.outerH - m.innerH);
  const maxW = m.availW - dw;
  const maxH = m.availH - dh;
  let vw = TV_WIDTH;
  let vh = TV_HEIGHT;
  if (maxW < TV_WIDTH || maxH < TV_HEIGHT) {
    vw = Math.max(16, Math.floor(Math.min(TV_WIDTH, maxW, (maxH * 16) / 9) / 16) * 16);
    vh = (vw * 9) / 16;
  }
  return { viewportW: vw, viewportH: vh, outerW: vw + dw, outerH: vh + dh };
}

/** `?tv=1` marks this window as the TV window; remembered for the tab so the Spotify login redirect keeps it. */
export function isTvWindow(): boolean {
  try {
    if (new URLSearchParams(location.search).get('tv') === '1') sessionStorage.setItem(TV_KEY, '1');
    return sessionStorage.getItem(TV_KEY) === '1';
  } catch {
    return false;
  }
}

/** Where the Spotify callback returns the host to. */
export function hostHref(): string {
  return isTvWindow() ? '/?as=host&tv=1' : '/?as=host';
}

/** In the TV window: size the viewport to 16:9. Best effort; browsers may refuse. */
export function fitTvWindow(win: Window = window): void {
  try {
    const t = computeTvTarget({
      innerW: win.innerWidth, innerH: win.innerHeight,
      outerW: win.outerWidth, outerH: win.outerHeight,
      availW: win.screen.availWidth, availH: win.screen.availHeight,
    });
    win.resizeTo(t.outerW, t.outerH);
    win.moveTo(0, 0);
  } catch { /* ignore */ }
}

/** The open TV window (set in the opener tab), or null. */
export const tvPopup: Writable<Window | null> = writable(null);
let watcher: ReturnType<typeof setInterval> | null = null;

function devFast(): boolean {
  try { return import.meta.env.DEV && sessionStorage.getItem(FAST_KEY) === '1'; } catch { return false; }
}

/** Opens the TV window; returns false when the browser blocked the pop-up. */
export function openTvWindow(): boolean {
  const url = buildTvUrl(location.origin, location.search, { nospotify: noSpotify(), fast: devFast() });
  const w = window.open(url, WINDOW_NAME, `popup=yes,width=${TV_WIDTH},height=${TV_HEIGHT},left=0,top=0`);
  if (!w) return false;
  tvPopup.set(w);
  if (watcher) clearInterval(watcher);
  watcher = setInterval(() => {
    if (w.closed) {
      if (watcher) clearInterval(watcher);
      watcher = null;
      tvPopup.set(null);
    }
  }, 1000);
  return true;
}
